import type { InputHTMLAttributes, ReactNode } from 'react'

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly id: string
  readonly label: string
  /** Rendered under the input and wired via aria-describedby. */
  readonly hint?: ReactNode
  /** Rendered as an error and wired via aria-describedby + aria-invalid. */
  readonly error?: string
}

/** The input classes, shared with `Textarea` so the two controls cannot drift apart. */
export function controlClassName(
  error: string | undefined,
  className?: string,
): string {
  return [
    'w-full rounded-lg border bg-surface text-base text-ink',
    'placeholder:text-ink-muted focus-visible:outline-2',
    'focus-visible:outline-offset-1 focus-visible:outline-focus',
    // The border is what identifies this as an input, so an error turns it red rather
    // than adding a second mark; the message under it carries the words.
    error ? 'border-danger' : 'border-line',
    className,
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * Label, input, hint and error as one unit.
 *
 * They are one component because the wiring between them (`htmlFor`,
 * `aria-describedby`, `aria-invalid`) is what makes a form usable with a screen reader,
 * and that wiring is exactly what gets forgotten when they are separate. The marketing
 * pages have no MUI, so this is the marketing subtree's own text input.
 */
export function Field({
  id,
  label,
  hint,
  error,
  className,
  ...props
}: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={controlClassName(
          error,
          `h-11 px-3 ${className ?? ''}`.trim(),
        )}
        {...props}
      />
      {hint ? (
        <p id={hintId} className="text-sm text-ink-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
